import { useEffect, useRef, useState } from 'react';
import './ModeSelector.css';
import { useLanguage } from '../../LanguageContext';

function ModeSelector({
  modeBarVisible,
  setModeBarVisible,
  activeMode,
  onFreeForm,
  onBeTheClient,
  onOars,
  onStrategies,
  variant = 'docked',
}) {
  const { t } = useLanguage();
  const [compactOpen, setCompactOpen] = useState(false);
  const compactRef = useRef(null);
  const isBtc = activeMode === 'btc';
  const isOars = activeMode === 'oars';
  const isStrategies = activeMode === 'strategies';
  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const containerClass = `mode-selector-container mode-selector-container--${isCompact ? 'compact' : isInline ? 'inline' : 'docked'}`;
  const activeModeLabel = isOars ? t('mode_oars') : isStrategies ? t('mode_strategies') : isBtc ? t('mode_btc') : t('mode_free_form');
  const modeOptions = [
    { id: 'oars', label: t('mode_oars'), active: isOars, onSelect: onOars },
    { id: 'strategies', label: t('mode_strategies'), active: isStrategies, onSelect: onStrategies },
    { id: 'freeform', label: t('mode_free_form'), active: !isBtc && !isOars && !isStrategies, onSelect: onFreeForm },
    { id: 'btc', label: t('mode_btc'), active: isBtc, onSelect: onBeTheClient },
  ];

  useEffect(() => {
    if (!compactOpen) return undefined;

    const handlePointerDown = (event) => {
      if (compactRef.current && !compactRef.current.contains(event.target)) {
        setCompactOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [compactOpen]);

  if (isCompact) {
    return (
      <div className={containerClass} ref={compactRef}>
        <button
          type="button"
          className="mode-compact-trigger"
          onClick={() => setCompactOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={compactOpen}
        >
          <span className="mode-compact-trigger__label">{activeModeLabel}</span>
          <svg className="mode-compact-trigger__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 9L12 15L18 9" />
          </svg>
        </button>
        {compactOpen && (
          <div className="mode-compact-menu" role="menu">
            {modeOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                role="menuitemradio"
                aria-checked={option.active}
                className={`mode-compact-menu__item${option.active ? ' active' : ''}`}
                onClick={() => {
                  if (!option.active && option.onSelect) option.onSelect();
                  setCompactOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {isInline || modeBarVisible ? (
        <div className="mode-selector-panel">
          <div className="mode-bar-header">
            <span className="mode-bar-header-spacer" aria-hidden="true" />
            <span className="mode-bar-title">{t('mode_title')}</span>
            {isInline ? (
              <span className="mode-bar-toggle-placeholder" aria-hidden="true" />
            ) : (
              <button
                type="button"
                className="mode-bar-toggle mode-bar-toggle-inline"
                onClick={() => setModeBarVisible(false)}
                title={t('mode_hide')}
                aria-label={t('mode_hide')}
              >
                <span className="mode-bar-toggle-inline-label">Collapse</span>
                <svg className="collapse-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9L12 15L18 9" />
                </svg>
              </button>
            )}
          </div>
          <div className="mode-bar-buttons">
            <button
              type="button"
              className={`mode-tab ${isOars ? 'mode-tab-active' : ''}`}
              onClick={() => !isOars && onOars && onOars()}
            >
              {t('mode_oars')}
            </button>
            <div className="mode-selector-divider" />
            <button
              type="button"
              className={`mode-tab ${isStrategies ? 'mode-tab-active' : ''}`}
              onClick={() => !isStrategies && onStrategies && onStrategies()}
            >
              {t('mode_strategies')}
            </button>
            <div className="mode-selector-divider" />
            <button
              type="button"
              className={`mode-tab ${!isBtc && !isOars && !isStrategies ? 'mode-tab-active' : ''}`}
              onClick={() => (isBtc || isOars || isStrategies) && onFreeForm && onFreeForm()}
            >
              {t('mode_free_form')}
            </button>
            <div className="mode-selector-divider--section" />
            <button
              type="button"
              className={`mode-tab mode-tab-btc ${isBtc ? 'mode-tab-active' : ''}`}
              onClick={() => onBeTheClient && onBeTheClient()}
            >
              {t('mode_btc')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="mode-bar-toggle mode-bar-toggle-collapsed"
          onClick={() => setModeBarVisible(true)}
          title={t('mode_show')}
          aria-label={t('mode_show')}
        >
          <span className="mode-bar-toggle-label">Expand Navigator</span>
          <svg className="collapse-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 15L12 9L18 15" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default ModeSelector;
